import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { RefObject } from 'react';

import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { TAIL_FALLBACK_PX, isAtTail } from '../tailFollow';
import { SHEET_BODY_SCROLLER } from './useSheetBodyScroller';

export interface TailFollow {
  /** Entries that arrived while the reader was not at the tail. */
  pending: number;
  /** Scroll to the tail and clear the count. */
  jumpToTail: () => void;
}

/**
 * Follow the tail of a growing list, or offer the way back to it (spec 097
 * FR-012 – FR-014).
 *
 * The scroller is the drawer's one scroller (`SheetBody`, spec 096), found from
 * the list rather than passed, so this hook cannot be pointed at the wrong box.
 * Where the reader was is recorded on every scroll — at the tail, within one
 * entry's height, or not — and consulted when the count grows: at the tail, the
 * view follows; anywhere else it stays put and the arrivals are counted until
 * the reader jumps or scrolls down themselves. Nothing here runs when the list
 * is not live.
 */
export function useTailFollow(
  listRef: RefObject<HTMLElement | null>,
  count: number,
  enabled: boolean,
): TailFollow {
  const reducedMotion = useReducedMotion();
  const wasAtTail = useRef(true);
  const previousCount = useRef(count);
  const [pending, setPending] = useState(0);

  const scrollerOf = useCallback(
    () => listRef.current?.closest<HTMLElement>(SHEET_BODY_SCROLLER) ?? null,
    [listRef],
  );

  const thresholdOf = useCallback(() => {
    const last = listRef.current?.querySelector<HTMLElement>('li:last-of-type');
    return last?.offsetHeight || TAIL_FALLBACK_PX;
  }, [listRef]);

  const scrollToTail = useCallback(
    (scroller: HTMLElement) => {
      if (typeof scroller.scrollTo === 'function') {
        scroller.scrollTo({
          top: scroller.scrollHeight,
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      } else {
        scroller.scrollTop = scroller.scrollHeight;
      }
    },
    [reducedMotion],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const scroller = scrollerOf();
    if (!scroller) return undefined;
    const onScroll = () => {
      const atTail = isAtTail(scroller, thresholdOf());
      wasAtTail.current = atTail;
      if (atTail) setPending(0);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
    };
  }, [enabled, scrollerOf, thresholdOf]);

  useLayoutEffect(() => {
    const before = previousCount.current;
    previousCount.current = count;
    if (!enabled || count <= before || before === 0) return;
    const scroller = scrollerOf();
    if (!scroller) return;
    if (wasAtTail.current) {
      scrollToTail(scroller);
    } else {
      setPending((current) => current + (count - before));
    }
  }, [count, enabled, scrollToTail, scrollerOf]);

  const jumpToTail = useCallback(() => {
    const scroller = scrollerOf();
    if (scroller) scrollToTail(scroller);
    wasAtTail.current = true;
    setPending(0);
  }, [scrollToTail, scrollerOf]);

  return { pending, jumpToTail };
}
